function findPropInsideObjects($keyword: string, $values: Array<object>): any {
  for (const v of $values) {
    if (v.hasOwnProperty($keyword)) {
      return v[$keyword];
    }
  }

  throw new Error('Not Found');
}

export function interpolate($msg: string, ...$values: Array<object>): string {
  const bracesApplied = ($msg || '').replace(/{{\s*(\S+?)\s*}}/g, function interpolateReplacer($match: string, $keyword: string): any {
    try {
      return findPropInsideObjects($keyword, $values);
    } catch (e) {
      return $match;
    }
  });

  return vsprintf(bracesApplied, $values);
}

const formatPatterns = {
  notString: /[^s]/,
  notBool: /[^t]/,
  notType: /[^T]/,
  notPrimitive: /[^v]/,
  number: /[diefg]/,
  numericArg: /[bcdiefguxX]/,
  json: /[j]/,
  notJson: /[^j]/,
  text: /^[^\x25]+/,
  modulo: /^\x25{2}/,
  placeholder: /^\x25(?:([1-9]\d*)\$|\(([^\)]+)\))?(\+)?(0|'[^$])?(-)?(\d+)?(?:\.(\d+))?([b-gijostTuvxX])/,
  key: /^([a-z_][a-z_\d]*)/i,
  keyAccess: /^\.([a-z_][a-z_\d]*)/i,
  indexAccess: /^\[(\d+)\]/,
  sign: /^[\+\-]/
};

export function sprintf($key: string, ...args: any[]): string {
  return sprintfFormat(sprintfParse($key), args);
}

export function vsprintf(fmt: string, argv: any[]): string {
  return sprintf.apply(null, [fmt].concat(argv || []));
}

function sprintfFormat($parseTree: ParsedTree, $args: any): string {
  const treeLength = $parseTree.length;

  let argsIdx = 0;
  let arg: any;
  let pad;
  let isPositive;

  let result = '';

  for (let i = 0; i < treeLength; i++) {
    const el: ParsedTreeEl = $parseTree[i];

    if (typeof el === 'string') {
      result += $parseTree[i];
    } else if (typeof el === 'object') {
      if (el.keys) { // keyword argument
        arg = $args[argsIdx];
        for (let k = 0; k < el.keys.length; k++) {
          if (arg === undefined || typeof arg !== 'object') {
            throw new Error(sprintf('[sprintf] Cannot access property "%s" of undefined value "%s"', el.keys[k], el.keys[k - 1]));
          }

          arg = arg[el.keys[k]];
        }
      } else if (el.paramNo) { // positional argument (explicit)
        arg = $args[el.paramNo];
      } else { // positional argument (implicit)
        arg = $args[argsIdx++];
      }

      if (formatPatterns.notType.test(el.type) && formatPatterns.notPrimitive.test(el.type) && arg instanceof Function) {
        arg = arg();
      }

      if (formatPatterns.numericArg.test(el.type) && (typeof arg !== 'number' && isNaN(arg))) {
        throw new TypeError(sprintf('[sprintf] expecting number but found %T', arg));
      }

      if (formatPatterns.number.test(el.type)) {
        isPositive = parseInt(arg, 10) >= 0;
      }

      switch (el.type) {
        case 'b':
          arg = parseInt(arg, 10).toString(2);
          break;
        case 'c':
          arg = String.fromCharCode(parseInt(arg, 10));
          break;
        case 'd':
        case 'i':
          arg = parseInt(arg, 10);
          break;
        case 'j':
          arg = JSON.stringify(arg, null, el.width > 0 ? el.width : 0);
          break;
        case 'e':
          arg = el.precision ? parseFloat(arg).toExponential(el.precision) : parseFloat(arg).toExponential();
          break;
        case 'f':
          arg = el.precision ? parseFloat(arg).toFixed(el.precision) : parseFloat(arg);
          break;
        case 'g':
          arg = el.precision ? String(Number(arg.toPrecision(el.precision))) : parseFloat(arg);
          break;
        case 'o':
            // tslint:disable-next-line:no-bitwise
          arg = (parseInt(arg, 10) >>> 0).toString(8);
          break;
        case 's':
          arg = String(arg);
          arg = (el.precision ? arg.substring(0, el.precision) : arg);
          break;
        case 't':
          arg = String(!!arg);
          arg = (el.precision ? arg.substring(0, el.precision) : arg);
          break;
        case 'T':
          arg = Object.prototype.toString.call(arg).slice(8, -1).toLowerCase();
          arg = (el.precision ? arg.substring(0, el.precision) : arg);
          break;
        case 'u':
            // tslint:disable-next-line:no-bitwise
          arg = parseInt(arg, 10) >>> 0;
          break;
        case 'v':
          arg = arg.valueOf();
          arg = (el.precision ? arg.substring(0, el.precision) : arg);
          break;
        case 'x':
            // tslint:disable-next-line:no-bitwise
          arg = (parseInt(arg, 10) >>> 0).toString(16);
          break;
        case 'X':
            // tslint:disable-next-line:no-bitwise
          arg = (parseInt(arg, 10) >>> 0).toString(16).toUpperCase();
          break;
      }

      if (formatPatterns.json.test(el.type)) {
        result += arg;
      } else {
        let sign = '';
        if (formatPatterns.number.test(el.type) && (!isPositive || el.sign)) {
          sign = isPositive ? '+' : '-';
          arg = arg.toString().replace(formatPatterns.sign, '');
        }

        const padCharacter = el.padChar ? el.padChar === '0' ? '0' : el.padChar.charAt(1) : ' ';
        const padLength = el.width - (sign + arg).length;
        pad = el.width ? (padLength > 0 ? padCharacter.repeat(padLength) : '') : '';
        result += el.align ? sign + arg + pad : (padCharacter === '0' ? sign + pad + arg : pad + sign + arg);
      }
    }
  }

  return result;
}

type ParsedTreeEl = {
  placeholder: string,
  paramNo: string,
  keys: string,
  sign: string,
  padChar: string,
  align: string,
  width: number,
  precision: number,
  type: string
} | string;

type ParsedTree = ParsedTreeEl[];

const sprintfParseCache: { [fmtString: string]: ParsedTree } = {};

function sprintfParse($fmt: string): ParsedTree {
  if (sprintfParseCache[$fmt]) {
    return sprintfParseCache[$fmt];
  }

  let fmtPart = $fmt;
  let match;
  const parseTree: ParsedTree = [];
  let argNamesBit = 0;

  while (fmtPart) {
    match = formatPatterns.text.exec(fmtPart);
    if (match !== null) {
      parseTree.push(match[0]);
    } else {
      match = formatPatterns.modulo.exec(fmtPart);
      if (match !== null) {
        parseTree.push('%');
      } else {
        match = formatPatterns.placeholder.exec(fmtPart);
        if (match !== null) {
          if (match[2]) {
              // tslint:disable-next-line:no-bitwise
            argNamesBit |= 1;

            const fieldList = [];
            let replacementField = match[2];
            let fieldMatch = [];

            fieldMatch = formatPatterns.key.exec(replacementField);
            if (fieldMatch !== null) {
              fieldList.push(fieldMatch[1]);

              replacementField = replacementField.substring(fieldMatch[0].length);
              while (replacementField !== '') {
                fieldMatch = formatPatterns.keyAccess.exec(replacementField);
                if (fieldMatch !== null) {
                  fieldList.push(fieldMatch[1]);
                } else {
                  fieldMatch = formatPatterns.indexAccess.exec(replacementField);
                  if (fieldMatch !== null) {
                    fieldList.push(fieldMatch[1]);
                  } else {
                    throw new SyntaxError('[sprintf] failed to parse named argument key');
                  }
                }
              }
            } else {
              throw new SyntaxError('[sprintf] failed to parse named argument key');
            }

            // @ts-ignore
              match[2] = fieldList;
          } else {
              // tslint:disable-next-line:no-bitwise
            argNamesBit |= 2;
          }
          if (argNamesBit === 3) {
            throw new Error('[sprintf] mixing positional and named placeholders is not (yet) supported');
          }

          parseTree.push({
            placeholder: match[0],
            paramNo: match[1],
            keys: match[2],
            sign: match[3],
            padChar: match[4],
            align: match[5],
            width: parseInt(match[6], 10),
            precision: parseInt(match[7], 10),
            type: match[8]
          });
        } else {
          throw new SyntaxError('[sprintf] unexpected placeholder');
        }
      }
    }

    fmtPart = fmtPart.substring(match[0].length);
  }

  return sprintfParseCache[$fmt] = parseTree;
}
